<template>
  <div class="flex h-100 flex-col flex-1">
    <FlotoCrudContainer
      :fetch-fn="getMyRequests"
      as-table
      :columns="columns"
      :show-no-data="false"
      allow-keyboard-navigation
      @select-item="handleViewDetail"
    >
      <template v-slot:add-controls="{ refreshList, items }">
        <FlotoPageHeader>
          <template v-slot:title>
            {{ $tc('my_request') }}
          </template>
          <template v-slot:title-append>
            <FlotoScrollDropdown
              v-model="selectedSearch"
              avoid-keyboard-navigation
              class="header-selector search-selector"
              :options="ticketSearchOptions"
              @change="refreshList"
            >
              <template v-slot:trigger="{ toggle }">
                <div
                  class="inline-block ml-4 text-neutral cursor-pointer md-text-base font-normal"
                  @click="toggle"
                >
                  {{
                    __t(
                      (
                        ticketSearchOptions.find(
                          (o) => o.key === selectedSearch
                        ) || {}
                      ).text
                    )
                  }}
                  <MIcon name="chevron-down" class="ml-1" />
                </div>
              </template>
              <template v-slot:menu-item="{ item, selectItem }">
                <a
                  href="javascript:;"
                  class="item-link flex-1"
                  @click.stop="selectItem(item)"
                >
                  {{ __t(item.text) }}
                </a>
              </template>
            </FlotoScrollDropdown>
          </template>
          <template v-slot:after-title>
            <FlotoLink
              v-if="allowRequesterToCreateIncidents"
              id="import-btn"
              class="mx-1"
              :to="
                $modules.getModuleRoute('support-portal', 'import-csv-list', {
                  params: {
                    moduleName: moduleName || $constants.REQUEST,
                  },
                })
              "
            >
              <MTooltip>
                <template v-slot:trigger>
                  <MButton
                    variant="neutral-lighter"
                    class="text-neutral"
                    :shadow="false"
                    shape="circle"
                  >
                    <MIcon name="file-import" />
                  </MButton>
                </template>
                {{ $t('import') }}
              </MTooltip>
            </FlotoLink>
            <ExportDrawer
              v-if="items.length > 0"
              :module-name="moduleName"
              :default-selected-columns="defaultSelectedColumns"
              :create-fn="handleExportSubmit"
              :supported-data-fn="getSupportedData"
            >
              <template v-slot:trigger="{ open }">
                <MTooltip>
                  <template v-slot:trigger>
                    <MButton
                      variant="neutral-lighter"
                      class="text-neutral mx-1"
                      :shadow="false"
                      shape="circle"
                      @click="open"
                    >
                      <MIcon name="file-export" />
                    </MButton>
                  </template>
                  {{ $t('export') }}
                </MTooltip>
              </template>
            </ExportDrawer>
            <MTooltip>
              <template v-slot:trigger>
                <MButton
                  class="mx-1"
                  variant="neutral-lighter"
                  shape="circle"
                  :shadow="false"
                  @click="refreshList"
                >
                  <MIcon name="sync" />
                </MButton>
              </template>
              {{ $t('refresh') }}
            </MTooltip>
            <ColumnSelector
              class="mx-1"
              :value="columns"
              :column-order="orderedColumns"
              :available-columns="allAvailableColumns"
              @change="($event) => updateColumnPreferences($event, refreshList)"
            />
          </template>
        </FlotoPageHeader>
        <MRow class="mt-1 my-4">
          <MCol>
            <FlotoSearchBar
              v-model="searchCriteria"
              :allow-save-search="false"
              :persist-criteria="false"
              :module-name="$constants.REQUEST"
              :exclude-search-params="getExcludedSearchParams"
              @change="refreshList"
            />
          </MCol>
        </MRow>
      </template>
      <template v-slot:form>
        <span />
      </template>
      <template v-slot:list-items="listSlotData">
        <div class="w-full flex flex-col flex-1">
          <MRow class="min-h-0 flex-no-wrap flex-1" :gutter="0">
            <MCol v-bind="{ size: 0 }" class="flex flex-col overflow-hidden">
              <FlotoFixedView :no-height="listSlotData.items.length !== 0">
                <FlotoScrollView>
                  <div
                    class="flex flex-col min-w-0"
                    :class="{
                      'flex-1': listSlotData.items.length === 0,
                    }"
                  >
                    <RequestListTable
                      v-if="listSlotData.items.length >= 0"
                      :key="`${columns.length}`"
                      :shrink-table="true"
                      :selectable="false"
                      :resource-name="$tc(moduleName)"
                      :columns="columns"
                      view="list"
                      show-no-data
                      v-bind="listSlotData"
                      @update="listSlotData.update"
                      @item-click="listSlotData.activateItem"
                    >
                      <template v-slot:row="rowSlotData">
                        <RequestTableRow
                          v-bind="rowSlotData"
                          :module-name="moduleName"
                        />
                      </template>
                    </RequestListTable>
                  </div>
                </FlotoScrollView>
              </FlotoFixedView>
            </MCol>
          </MRow>
        </div>
      </template>
    </FlotoCrudContainer>
  </div>
</template>

<script>
import Pick from 'lodash/pick'
import {
  transformBinaryQualForServer,
  buildRelationalQualificationStructure,
  buildFlatQualificationStructure,
} from '@data/qualification'
import { PageComputed, PageMethods } from '@state/modules/page'
import { SupportPortalConfigComputed } from '@state/modules/support-portal-config'
import { getMyRequestsApi } from '../support-portal-api'
import { StatusComputed } from '../../../state/modules/status'
import { authComputed } from '@state/modules/auth'
import { getFieldByRequesterAccess } from '@modules/support-portal/helpers/field-access'
import {
  PreferenceComputed,
  PreferenceMethods,
} from '@state/modules/preference'
import ColumnSelector from '@components/crud/column-selector'
import { getAvailableColumnsForTable } from '../helpers/request-table-columns'
import { flattenFields } from '@data/form'
import { FormComputed } from '@state/modules/form'
import RequestListTable from '../components/request-list-table'
import RequestTableRow from '../components/request-list-table-row'
import ExportDrawer from '@components/export/export-drawer'
import { getEncryptedPassword } from '@utils/password'
// import { transformSearchCriteriasForServer } from '@data/search'
import {
  exportDataApi,
  getExportAvailableColumnsApi,
} from '@modules/ticket/ticket-api'

export default {
  name: 'MyRequests',
  components: {
    ColumnSelector,
    RequestListTable,
    RequestTableRow,
    ExportDrawer,
  },
  data() {
    this.ticketSearchOptions = [
      { text: 'all_request', key: 'all_request' },
      { text: 'all_open_inprogress', key: 'all_open_inprogress' },
      { text: 'all_pending', key: 'all_pending' },
      { text: 'all_resolved_closed', key: 'all_resolved_closed' },
    ]
    this.moduleName = this.$constants.REQUEST
    return {
      searchCriteria: undefined,
      selectedSearch: 'all_request',
      allAvailableColumns: [],
      columns: [],
      orderedColumns: [],
    }
  },
  computed: {
    ...StatusComputed,
    ...SupportPortalConfigComputed,
    ...PreferenceComputed,
    ...FormComputed,
    ...authComputed,
    ...Pick(PageComputed, [
      'requestSearches',
      'requestSelectSearch',
      'requestSearchCriterias',
    ]),
    openStatusId() {
      return this.requestFindStatus('open').id
    },
    resolvedStatusId() {
      return this.requestFindStatus('resolved').id
    },
    closedStatusId() {
      return this.requestFindStatus('closed').id
    },
    defaultSelectedColumns() {
      return this.columns.map((c) => c.key)
    },
    getExcludedSearchParams() {
      return [
        'requesterId',
        this.allowRequesterToAccestTicketApprovalTab ? '' : 'approvalStatus',
      ]
    },
    getAccessViolatedColumns() {
      let hideColumns = []
      if (!this.allowRequesterToAccestTicketApprovalTab) {
        hideColumns.push('approvalStatus')
      }
      return hideColumns
    },
  },
  watch: {
    requestForm: {
      immediate: true,
      handler() {
        this.prepareAvailableColumns()
      },
    },
    userPreferences: {
      immediate: true,
      handler: 'changeOrderedColumns',
    },
  },
  methods: {
    ...PreferenceMethods,
    ...PageMethods,
    updateColumnPreferences(value, refreshFn) {
      const columns = value.map((v) => v.key)
      this.updateModulePreference({
        moduleName: this.moduleName,
        columnsForSupportPortal: columns,
      }).then(() => refreshFn())
    },
    changeOrderedColumns() {
      this.orderedColumns = Object.freeze(
        this.defaultListingColumnsForSupportPortal(this.$constants.REQUEST)
      )
      this.columns = this.orderedColumns
        .map((key) => this.allAvailableColumns.find((c) => c.key === key))
        .filter(Boolean)
    },
    prepareAvailableColumns() {
      const allFields = flattenFields(this['requestForm'].fields || [])
      const filteredFields = allFields.filter(
        (f) =>
          [
            'text',
            'radio',
            'dropdown',
            'dependent',
            'datetime',
            'number',
            'SystemFieldRest',
          ].indexOf(f.type) >= 0 &&
          f.useOnPortal &&
          // @TODO Remove subject and status condition from here and try to handle it in table columns file
          [
            'templateId',
            'fileAttachments',
            'subject',
            // 'statusId',
            'requester',
            'description',
            'ccEmailSet',
            'tags',
            'transitionModelId',
          ].indexOf(f.paramName) === -1
      )
      const flattenFilteredFields = flattenFields(filteredFields)
      let fields = getFieldByRequesterAccess(
        flattenFilteredFields,
        this.user
      ).filter((f) => f.inputType !== 'vendor')
      this.allAvailableColumns = Object.freeze(
        getAvailableColumnsForTable(fields, this.getAccessViolatedColumns)
      )
    },
    handleViewDetail(item) {
      this.$router.push(
        this.$currentModule.getRoute('view-request', {
          params: { id: item.id },
        })
      )
    },
    // @TODO - Used the status obtained instead of currentReadOnlyStatus , computed code can be reusable

    // getReadOnlyStatus(statusId) {
    //   const excludedStatusIds = [
    //     this.closedStatusId,
    //     this.openStatusId,
    //     this.resolvedStatusId,
    //   ]
    //   if (excludedStatusIds.indexOf(statusId) >= 0) {
    //     return statusId
    //   }
    //   const allStatus = this.requestStatus
    //   const currentStatus = allStatus.find((s) => s.key === statusId)
    //   if (currentStatus) {
    //     if (currentStatus.runSla) {
    //       return (this.requestFindStatus('in progress') || {}).id
    //     } else {
    //       return (this.requestFindStatus('pending') || {}).id
    //     }
    //   }
    //   return undefined
    // },
    buildSearchQualification() {
      switch (this.selectedSearch) {
        case 'all_requests':
          return []
        case 'all_open_inprogress':
          return [
            transformBinaryQualForServer(
              buildRelationalQualificationStructure(
                'statusId',
                'in',
                'in_progress',
                'variable',
                'db'
              ),
              buildRelationalQualificationStructure(
                'statusId',
                'equal',
                this.openStatusId,
                'long',
                'db'
              ),
              'or'
            ),
          ]
        case 'all_pending':
          return [
            buildRelationalQualificationStructure(
              'statusId',
              'in',
              'pending',
              'variable',
              'db'
            ),
          ]
        case 'all_resolved_closed':
          return [
            buildRelationalQualificationStructure(
              'statusId',
              'in',
              [this.resolvedStatusId, this.closedStatusId],
              'long',
              'db'
            ),
          ]
        default:
          return []
      }
    },
    getMyRequests(limit, offset) {
      let criterias
      if (this.searchCriteria) {
        criterias = {
          ...this.searchCriteria,
          quals: [
            ...this.searchCriteria.quals,
            ...this.buildSearchQualification(),
          ],
        }
      } else {
        criterias = buildFlatQualificationStructure(
          this.buildSearchQualification()
        )
      }
      return getMyRequestsApi(criterias, limit, offset)
      // @TODO - Used the status obtained instead of currentReadOnlyStatus , computed code can be reusable

      // .then((data) => {
      //   return {
      //     ...data,
      //     items: data.items.map((x) => ({
      //       ...x,
      //       readOnlyStatusId: this.getReadOnlyStatus(x.statusId),
      //     })),
      //   }
      // })
    },
    getSupportedData() {
      return getExportAvailableColumnsApi(this.$constants.REQUEST, {})
    },
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleExportSubmit(data) {
      const requestData = {}
      let criterias
      if (this.searchCriteria) {
        criterias = {
          ...this.searchCriteria,
          quals: [
            ...this.searchCriteria.quals,
            ...this.buildSearchQualification(),
          ],
        }
      } else {
        criterias = buildFlatQualificationStructure(
          this.buildSearchQualification()
        )
      }
      const qualification = criterias
      requestData.model = this.moduleName
      requestData.exportFormat = data.exportFormat
      requestData.enabled = data.enabled
      requestData.attachmentPassword = this.getAvailableAttachmentPassword(data)
      const requestedColumns = []
      data.columns.forEach((c, i) =>
        requestedColumns.push({ propKey: c, orderNumber: i + 1 })
      )
      requestData.columns = requestedColumns
      requestData.qualification = this.defaultQualifications || qualification
      return exportDataApi(requestData)
    },
  },
}
</script>
